'use client';

// ---------------------------------------------------------------------------
// ChangelogDrawer — opens after a software update reload to show release notes.
// Auto-opens when localStorage('hms-just-updated') is set and the version
// hasn't been marked as seen in localStorage('hms-changelog-seen').
// ---------------------------------------------------------------------------

import { useEffect, useState, useCallback } from 'react';
import { Drawer } from '@/shared/ui/Drawer';
import { useTranslation } from '@/core/i18n';
import {
  CHANGELOG,
  CURRENT_VERSION,
  type ChangelogEntry,
  type ChangelogItemType,
} from '@/core/config/changelog';

const STORAGE_KEY_UPDATED = 'hms-just-updated';
const STORAGE_KEY_SEEN = 'hms-changelog-seen';

function getSeenVersions(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SEEN);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function markVersionSeen(version: string): void {
  const seen = getSeenVersions();
  if (!seen.includes(version)) {
    seen.push(version);
    localStorage.setItem(STORAGE_KEY_SEEN, JSON.stringify(seen));
  }
}

function TypeIcon({ type }: { type: ChangelogItemType }) {
  if (type === 'feature') {
    return (
      <svg id="sw-changelog-icon-feature" width="16" height="16" className="h-4 w-4 text-hms-gold shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    );
  }
  if (type === 'fix') {
    return (
      <svg id="sw-changelog-icon-fix" width="16" height="16" className="h-4 w-4 text-hms-teal shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    );
  }
  return (
    <svg id="sw-changelog-icon-improvement" width="16" height="16" className="h-4 w-4 text-hms-blue shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}

function VersionBlock({ entry, t }: { entry: ChangelogEntry; t: (key: string) => string }) {
  return (
    <div id={`sw-changelog-version-${entry.version}`} className="mb-6 last:mb-0">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-hms-text">
          {t('shared.changelog.version')} {entry.version}
        </span>
        <span className="text-[11px] text-hms-textDim">
          {entry.date}
        </span>
      </div>
      <ul className="space-y-2">
        {entry.items.map((item) => (
          <li key={item.key} className="flex items-start gap-2.5">
            <TypeIcon type={item.type} />
            <div className="min-w-0">
              <span className="text-[11px] font-medium uppercase tracking-wide text-hms-textDim">
                {t(`shared.changelog.${item.type}`)}
              </span>
              <p className="text-[13px] text-hms-textSub leading-relaxed">
                {t(item.key)}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ChangelogDrawer() {
  const [open, setOpen] = useState(false);
  const [updatedVersion, setUpdatedVersion] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const justUpdated = localStorage.getItem(STORAGE_KEY_UPDATED);
    if (!justUpdated) return;

    localStorage.removeItem(STORAGE_KEY_UPDATED);
    const seen = getSeenVersions();
    if (!seen.includes(justUpdated)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only initialization from localStorage
      setUpdatedVersion(justUpdated);
      setOpen(true);
    }
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    if (updatedVersion) {
      markVersionSeen(updatedVersion);
    }
  }, [updatedVersion]);

  const currentEntry = CHANGELOG.find(
    (e) => e.version === (updatedVersion ?? CURRENT_VERSION)
  );
  const olderEntries = CHANGELOG.filter(
    (e) => e.version !== (updatedVersion ?? CURRENT_VERSION)
  );

  return (
    <Drawer
      id="sw-changelog-drawer"
      open={open}
      onClose={handleClose}
      title={t('shared.changelog.title')}
      description={`${t('shared.changelog.released')} ${updatedVersion ?? CURRENT_VERSION}`}
      size="md"
      footer={
        <button
          id="sw-changelog-close-btn"
          type="button"
          onClick={handleClose}
          className="w-full rounded-lg bg-hms-gold px-4 py-2 text-sm font-semibold text-hms-bg hover:bg-hms-goldLt transition-colors"
        >
          {t('shared.changelog.gotIt')}
        </button>
      }
    >
      <div id="sw-changelog-content">
        {currentEntry && <VersionBlock entry={currentEntry} t={t} />}
        {olderEntries.length > 0 && (
          <>
            <div className="my-4 border-t border-hms-border" />
            <p className="text-[11px] font-medium uppercase tracking-wide text-hms-textDim mb-3">
              {t('shared.changelog.previousVersions')}
            </p>
            {olderEntries.map((entry) => (
              <VersionBlock key={entry.version} entry={entry} t={t} />
            ))}
          </>
        )}
      </div>
    </Drawer>
  );
}
